import { memo, useCallback } from 'react';
import { FileTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, CopyText, Flex, FlexProps, Input, Label, Text, TextField } from '@/components/core';
import { ImportIcon } from '@/components/icons';
import { PROFILE_PICTURE_MIME_TYPES } from '@/constants';
import { useDebounceCallback, useImageSrc } from '@/hooks';
import { useOrganizationStore } from '@/store';

export type WorkspaceDetailsProps = object &
  FlexProps & {
    showWorkspaceId?: boolean;
  };

const IWorkspaceDetails = ({ showWorkspaceId, className }: WorkspaceDetailsProps) => {
  const organizationId = useOrganizationStore((state) => state.organizationId);
  const name = useOrganizationStore((state) => state.name);
  const pictureUrl = useOrganizationStore((state) => state.pictureUrl);
  const picture = useOrganizationStore((state) => state.picture);
  const { src } = useImageSrc({ source: pictureUrl ?? picture });
  const update = useOrganizationStore((state) => state.update);

  const onSelect = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      const files = Array.from(fileList);
      if (!files) return;
      update({ picture: files[0] });
    },
    [update]
  );

  const onUpdate = useDebounceCallback(
    useCallback((name: string) => update({ name }), [update]),
    200
  );

  return (
    <Flex variant="plain" col className={merge('gap-4 rounded-lg bg-white p-4', className)}>
      <Flex col className="gap-sm">
        <Label>Workspace icon</Label>

        <Flex className="gap-sm">
          <img className="size-14 rounded-lg" alt={organizationId ?? undefined} src={src} />

          <Flex col className="gap-xs">
            <FileTrigger allowsMultiple={false} acceptedFileTypes={PROFILE_PICTURE_MIME_TYPES} onSelect={onSelect}>
              <Button className="w-fit" variant="secondary">
                <ImportIcon />
                <Text>Upload new</Text>
              </Button>
            </FileTrigger>

            <Text size="xs">.jpeg, .png and .webp up to 10MB.</Text>
          </Flex>
        </Flex>
      </Flex>

      <TextField className="gap-xs flex flex-col" defaultValue={name ?? undefined} onChange={onUpdate}>
        <Label>Workspace name</Label>
        <Input id="name" placeholder="Acme Inc" />
      </TextField>

      {showWorkspaceId && (
        <Flex col className="gap-xs">
          <Label>Workspace ID</Label>
          <CopyText value={organizationId} />
        </Flex>
      )}
    </Flex>
  );
};

export const WorkspaceDetails = memo(IWorkspaceDetails) as typeof IWorkspaceDetails;
